import { Injectable, inject } from '@angular/core';
import { StorageService } from './storage.service';

/** localStorage key: map of taskId -> epoch ms the user last saw its comments. */
const SEEN_KEY = 'task-comment-last-seen';

/**
 * Device-local "new comment" awareness for tasks.
 *
 * There is no server-side read/seen state, so we track — per device — the
 * timestamp at which the user last opened each task's comments. A task has
 * "new comments" when its newest comment is newer than that timestamp, OR
 * when the user has never opened it (so existing, never-seen comments are
 * surfaced too). Opening a task clears its marker.
 *
 * Because it's device-local, seeing a task on one device won't clear the
 * marker on another (a future backend read-state would fix that).
 */
@Injectable({ providedIn: 'root' })
export class TaskCommentReadStateService {

    private readonly storage = inject(StorageService);

    private seenByTask: Record<string, number> | null = null;

    /** Newest comment timestamp on a task (epoch ms), or 0 when it has none. */
    public latestCommentTime(task: { comments?: any[] } | null | undefined): number {
        const comments = task?.comments ?? [];
        let max = 0;
        for (const comment of comments) {
            const raw = comment?.updatedAt ?? comment?.createdAt;
            const time = raw ? new Date(raw).getTime() : 0;
            if (!isNaN(time) && time > max) {
                max = time;
            }
        }
        return max;
    }

    /** True when the task has comment activity the user hasn't seen yet. */
    public hasNewComments(task: { id?: number; comments?: any[] } | null | undefined): boolean {
        if (!task?.id || !(task.comments?.length)) {
            return false;
        }
        this.ensureLoaded();
        // Never opened → 0, so any task with comments counts as new until seen.
        const seen = this.seenByTask![String(task.id)] ?? 0;
        return this.latestCommentTime(task) > seen;
    }

    /**
     * Record that the user has now seen this task's comments (clears "new").
     *
     * The baseline is the newest comment's timestamp — NOT {@code Date.now()}.
     * Both sides of the {@link hasNewComments} comparison must come from the same
     * (server) clock; mixing in the device clock lets clock skew or a device
     * timezone other than the server's push the baseline past later comments, so
     * a comment posted after you read the task would never re-flag it.
     */
    public markSeen(task: { id?: number; comments?: any[] } | null | undefined): void {
        if (!task?.id) {
            return;
        }
        this.ensureLoaded();
        this.seenByTask![String(task.id)] = this.latestCommentTime(task);
        this.storage.saveLocalData(SEEN_KEY, this.seenByTask);
    }

    /** Mark every given task's comments as seen in one go ("mark all as read"). */
    public markAllSeen(tasks: Array<{ id?: number; comments?: any[] }> | null | undefined): void {
        if (!tasks?.length) {
            return;
        }
        this.ensureLoaded();
        for (const task of tasks) {
            if (task?.id) {
                // Newest comment time only — see markSeen for why the device clock
                // must stay out of the "seen" baseline.
                this.seenByTask![String(task.id)] = this.latestCommentTime(task);
            }
        }
        this.storage.saveLocalData(SEEN_KEY, this.seenByTask);
    }

    private ensureLoaded(): void {
        if (this.seenByTask === null) {
            this.seenByTask = this.storage.getLocalData(SEEN_KEY) ?? {};
        }
    }
}
